import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip, Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import urlValidator from 'utils/validators/urlValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import phoneMask from 'utils/constants/phoneMask'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'

export const BigoAdsSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    email: Yup.string()
      .test(
        'emailValidator',
        'common::validation_messages.incorrect_email_format',
        emailValidator,
      )
      .required('common::validation_messages.required'),
    product_url: Yup.string()
      .required('common::validation_messages.required')
      .test('urlValidator', '', urlValidator),
    creative_url: Yup.string()
      .required('common::validation_messages.required')
      .test('urlValidator', '', urlValidator),
    license_url: Yup.string().test('urlValidator', '', urlValidator),
    legal_entity_name: Yup.string().required(
      'common::validation_messages.required',
    ),
    legal_entity_address: Yup.string().required(
      'common::validation_messages.required',
    ),
    phone: Yup.string()
      .nullable()
      .test(
        'phoneValidate',
        'common::validation_messages.incorrect_phone_format',
        function test(value) {
          const { path, createError } = this

          if (!value || value.length === 0) {
            return true
          }

          return (
            value.match(/^\+\d{7,15}$/) ||
            createError({
              path,
              message: 'common::validation_messages.incorrect_phone_format',
            })
          )
        },
      ),
  })

let CreateBigoAdsAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit(
    form,
    onClose,
    multiLogins,
  )

  const { email, user } = useSelector((state) => ({
    email: selector(state, 'email'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  return (
    <>
      <Typography variant={'Header-60'} className={'margin-bottom-16'}>
        {t('services::networks.' + networkType)}
      </Typography>

      <Typography
        variant={'Body-20'}
        color="text-secondary"
        className={'margin-bottom-16'}
      >
        {t('services::forms.register_account.form_subtitle')}
      </Typography>

      <form onSubmit={handleSubmit(submitAccountData)} noValidate>
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={
              'services::forms.register_account.bigo-ads.fields.login.label'
            }
          />
        </div>

        <div
          className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}
        >
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.bigo-ads.fields.email.label',
            )}
            t={t}
          />

          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.bigo-ads.fields.email.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div
          className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}
        >
          <Field
            name="legal_entity_name"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.bigo-ads.fields.legal_entity_name.label',
            )}
            t={t}
          />

          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.bigo-ads.fields.legal_entity_name.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="legal_entity_address"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.bigo-ads.fields.legal_entity_address.label',
            )}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            fullWidth
            normalize={phoneFieldNormalize}
            name="phone"
            component={renderMaskedInput}
            placeholderChar={'\u2002'}
            placeholder={'+ ( )'}
            mask={phoneMask}
            label={t(
              'services::forms.register_account.bigo-ads.fields.phone.label',
            )}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="product_url"
            type="product_url"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.bigo-ads.fields.product_url.label',
            )}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="creative_url"
            type="creative_url"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.bigo-ads.fields.creative_url.label',
            )}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="license_url"
            type="license_url"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.bigo-ads.fields.license_url.label',
            )}
            t={t}
          />
        </div>

        <SubmitButtons submitting={submitting} onClose={onClose} />
      </form>
    </>
  )
}

CreateBigoAdsAccountForm = reduxForm({
  form: 'CreateBigoAdsAccountForm',
  initialValues: {
    network: '',
    login: '',
    email: '',
    product_url: '',
    creative_url: '',
    license_url: '',
    legal_entity_name: '',
    legal_entity_address: '',
    phone: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateBigoAdsAccountForm)

CreateBigoAdsAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
}

export default CreateBigoAdsAccountForm
